import {ToolsService} from './tools.service';
import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Router, UrlSegment} from '@angular/router';
import {AllowedParamGroup} from '../types/AllowedParamGroup';
import {QueryParams} from '../types/QueryParams';
import {AllowedParam} from '../types/AllowedParam';
import {RedirectService} from './redirect.service';

@Injectable()
export class UrlValidatorService {
  constructor(
    private tools: ToolsService,
    private router: Router,
    private redirect: RedirectService,
  ) {}

  public cleanUpUrlAndRedirect(
    snapshot: ActivatedRouteSnapshot,
    routingBase: string[],
    allowedParamsGroups: AllowedParamGroup[],
    allowedQueryParams: string[],
  ): boolean {
    const filteredQueryParams: QueryParams = this.filterQueryParams(snapshot.queryParams, allowedQueryParams);

    const fullUrl = snapshot['_routerState'].url;
    const urlTree = this.router.parseUrl(fullUrl);
    const urlSegments = urlTree.root.children['primary']?.segments || [];
    urlSegments.shift();
    const filteredParams: string[] = this.filterParams(urlSegments, allowedParamsGroups);
    if (this.isRedirectNecessary(filteredQueryParams, snapshot.queryParams, filteredParams, urlSegments)) {
      this.redirectToFilteredUrl(filteredQueryParams, filteredParams, routingBase);
      return true;
    }
    return false;
  }

  private redirectToFilteredUrl(queryParams: QueryParams, params: string[], routingBase: string[]): void {
    const routerLink: string[] = routingBase.concat(params);
    console.log('Redirect to filtered URL: ', routerLink, queryParams);
    this.redirect.redirect(routerLink, queryParams);
  }

  private isRedirectNecessary(
    filteredQueryParams: QueryParams,
    originalQueryParams: QueryParams,
    filteredParams: string[],
    originalUrlSegments: UrlSegment[],
  ): boolean {
    return (
      Object.keys(filteredQueryParams).length !== Object.keys(originalQueryParams).length ||
      filteredParams.length !== originalUrlSegments.length
    );
  }

  private filterQueryParams(queryParams: QueryParams, allowedQueryParams: string[]): QueryParams {
    const filteredQueryParamKeys: string[] = Object.keys(queryParams).filter(
      (value) => allowedQueryParams.indexOf(value) !== -1,
    ) as string[];
    const filteredQueryParams: QueryParams = {};
    filteredQueryParamKeys.forEach((key) => {
      filteredQueryParams[key] = queryParams[key];
    });
    return filteredQueryParams;
  }

  private filterParams(urlSegments: UrlSegment[], allowedParamsGroups: AllowedParamGroup[]): string[] {
    let filteredParams: string[] = [];
    if (this.isFullMatch(urlSegments, allowedParamsGroups) || urlSegments.length === 0) {
      filteredParams = this.getParamsFromUrlSegments(urlSegments);
    } else {
      const partialMatchedGroup: AllowedParamGroup = this.getPartialMatchedGroup(urlSegments, allowedParamsGroups);
      if (!this.tools.isNil(partialMatchedGroup)) {
        filteredParams = this.getParamsFromUrlSegments(urlSegments.slice(0, partialMatchedGroup.params.length));
      }
    }
    return filteredParams;
  }

  private isFullMatch(urlSegments: UrlSegment[], allowedParamsGroups: AllowedParamGroup[]): boolean {
    let isMatched: boolean = false;
    allowedParamsGroups.forEach((group) => {
      if (group.params.length === urlSegments.length && isMatched === false) {
        isMatched = this.isGroupFullMatched(urlSegments, group);
      }
    });
    return isMatched;
  }

  private isGroupFullMatched(urlSegments: UrlSegment[], group: AllowedParamGroup): boolean {
    let isMatched: boolean = true;
    urlSegments.forEach((segment, index) => {
      if (isMatched === true) {
        isMatched = this.isItemMatched(segment, group.params[index]);
      }
    });
    return isMatched;
  }

  private getPartialMatchedGroup(
    urlSegments: UrlSegment[],
    allowedParamsGroups: AllowedParamGroup[],
  ): AllowedParamGroup {
    let partialMatch: boolean = false;
    let groupPartialMatched: AllowedParamGroup = null;
    allowedParamsGroups.forEach((group) => {
      if (partialMatch === false) {
        partialMatch = this.isGroupPartialMatched(urlSegments, group);
        if (partialMatch === true) {
          groupPartialMatched = group;
        }
      }
    });
    return groupPartialMatched;
  }

  private isGroupPartialMatched(urlSegments: UrlSegment[], group: AllowedParamGroup): boolean {
    let isMatched: boolean = true;
    group.params.forEach((groupItem, index) => {
      if (isMatched === true) {
        isMatched = this.isItemMatched(urlSegments[index], groupItem);
      }
    });
    return isMatched;
  }

  private isItemMatched(urlSegment: UrlSegment, allowedParam: AllowedParam): boolean {
    let isMatched: boolean = false;
    if (!this.tools.isNil(urlSegment)) {
      isMatched = urlSegment.path === allowedParam.value;
      if (allowedParam.value === null) {
        if (allowedParam.type === 'number') {
          isMatched = parseInt(urlSegment.path, 10).toString() === urlSegment.path;
        } else if (allowedParam.type === 'id-slug') {
          isMatched = !!urlSegment.path.match(/^(\d+)-?(.*)$/);
        } else {
          isMatched = true;
        }
      }
    }
    return isMatched;
  }

  private getParamsFromUrlSegments(urlSegments: UrlSegment[]): string[] {
    const params: string[] = [];
    urlSegments.forEach((segment) => params.push(segment.path));
    return params;
  }
}
